import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { 
  Box, Container, Typography, Button, Stack, Paper, alpha, 
  useTheme 
} from '@mui/material';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Analytics } from '@vercel/analytics/react';

// استيراد الصور كمتغيرات
import img1 from './images/1.webp';
import img2 from './images/2.webp';
import img3 from './images/3.webp';
import img4 from './images/4.webp';

// Icons
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import InfoIcon from '@mui/icons-material/Info';
import VerifiedIcon from '@mui/icons-material/Verified';

// الخطوط
import "@fontsource/sn-pro/400.css";
import "@fontsource/sn-pro/600.css";
import "@fontsource/sn-pro/700.css";

const COLORS = {
  primary: '#002a68',
  secondary: '#00d2ff',
  accent: '#25D366',
  text: '#1e2a3e',
  bgLight: '#f8fafc'
};

// --- رسمة السماعة الطبية (Hearing Aid) بالـ CSS مع أمواج من اليمين إلى اليسار (محدثة) ---
const HearingAidAnimation = () => (
  <Box sx={{ position: 'relative', width: 360, height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
    
     {/* جسم السماعة الطبية */}
     <Box sx={{
      width: 70, height: 170, 
      backgroundColor: '#e2e8f0', 
      borderRadius: '15px 70px 50px 15px / 120px 120px 60px 60px', 
      position: 'relative',
      zIndex: 3,
      boxShadow: 'inset -8px -8px 16px rgba(0,0,0,0.1), 6px 6px 12px rgba(0,0,0,0.2)', 
      transform: 'rotate(-15deg) translateX(40px)', 
      '&::before': { 
        content: '""', position: 'absolute', top: 30, left: -4, width: 12, height: 24, 
        backgroundColor: '#94a3b8', borderRadius: 8,
      }
    }}>
      {/* الأنبوب البلاستيكي الشفاف */}
      <Box sx={{
        position: 'absolute', top: -50, left: -70, width: 90, height: 100, 
        border: '8px solid transparent', 
        borderTopColor: 'rgba(255,255,255,0.6)',
        borderLeftColor: 'rgba(255,255,255,0.6)',
        borderRadius: '50%',
        transform: 'rotate(45deg)',
        filter: 'drop-shadow(0px 4px 2px rgba(0,0,0,0.1))'
      }} />
      {/* القالب (Earpiece) */}
      <Box sx={{
        position: 'absolute', top: 10, left: -80, width: 24, height: 36, 
        backgroundColor: '#f1f5f9', borderRadius: '50%',
        boxShadow: 'inset -4px -4px 8px rgba(0,0,0,0.1)'
      }} />
    </Box>

    {/* التموجات الصوتية */}
    {[1, 2, 3].map((i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, scale: 4, x: 200 }} 
        animate={{ 
          opacity: [0, 1, 0], 
          scale: [4, 2.4, 1], 
          x: [200, 100, 20] 
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          delay: i * 0.7,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          right: '30%', 
          width: 80, 
          height: 160, 
          borderRight: `8px solid snow`, 
          borderRadius: '0 100% 100% 0 / 0 50% 50% 0', 
          zIndex: 1
        }}
      />
    ))}
  </Box>
);

// --- زر الواتساب العائم ---
const WhatsAppFloatingBtn = () => (
  <Button
    variant="contained"
    startIcon={<WhatsAppIcon sx={{ ml: 1, fontSize: '1.5rem !important' }} />}
    href="https://wa.me/963988448687"
    target="_blank"
    sx={{
      position: 'fixed', bottom: 30, right: 30,
      background: COLORS.accent, color: 'white',
      px: { xs: 3, md: 3 }, py: 1.5, borderRadius: 10, fontSize: { xs: '1rem', md: '1.1rem' },
      fontWeight: 'bold', zIndex: 1000,
      boxShadow: '0 10px 25px rgba(37, 211, 102, 0.4)',
      '&:hover': { background: '#128C7E', transform: 'translateY(-3px)' },
      transition: 'all 0.3s ease'
    }}
  >
    راسلنا على واتساب
  </Button>
);

const HomePage = () => {
  const theme = useTheme();

  return (
    <Container sx={{ direction: 'rtl', bgcolor: 'white' }}>
      
      {/* 1. Hero Section */}
      <Box sx={{ 

  minHeight: '100vh', 
  display: 'flex', 
  alignItems: 'center',
  background: `linear-gradient(to right, ${alpha(COLORS.primary, 0.7)}, ${alpha(COLORS.primary, 0.95)}), url(${img4})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',

  color: 'white',
  pt: { xs: 2, md: 0 },
  overflow: 'hidden'
}}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          {/* استبدال الـ Grid بـ Box Flex للقسم الأول */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            alignItems: 'center', 
            gap: 4 
          }}>
            <Box sx={{ width: { xs: '100%', md: '60%' } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <Typography variant="h4" sx={{ fontSize: { xs: '2.8rem', md: '4.5rem' }, fontWeight: 600, mb: 2, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                  نقدم أفضل خدمات السمع بأسعار تناسب الجميع
                </Typography>
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 400, color: '#e2e8f0', lineHeight: 1.6 }}>
                  مركز الأطلال للسمع.. حيث تلتقي التكنولوجيا المتقدمة بالخبرة الطبية لنقدم لك صفاء السمع الذي تستحقه.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <WhatsAppFloatingBtn />
                </Stack>
              </motion.div>
            </Box>
            <Box sx={{ width: { xs: '100%', md: '40%' }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, delay: 0.3 }}>
                <HearingAidAnimation />
              </motion.div>
              
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 2. قسم الشعار والأصالة */}
      <Box id="about" sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          {/* تم التعديل هنا: استخدام Box flex-row-reverse ليكون اللابتوب (صورة يمين، نص يسار) والموبايل عمودي */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column-reverse', md: 'row-reverse' }, 
            alignItems: 'center', 
            gap: 6 
          }}>
            {/* حاوية الصورة على اليمين في اللابتوب */}
            <Box sx={{ width: { xs: '100%', md: '45%' } }}>
              <Paper elevation={0} sx={{ 
                borderRadius: 4, 
                overflow: 'hidden', 
                boxShadow: '0 20px 40px rgba(0,0,0,0.08)'
              }}>
                <img src={img2} alt="شعار الأطلال" style={{ width: '100%', height: 'auto', display: 'block' }} />
              </Paper>
            </Box>

            {/* حاوية النصوص على اليسار في اللابتوب */}
            <Box sx={{ width: { xs: '100%', md: '55%' } }}>
              <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          
                <Typography variant="h3" sx={{ fontWeight: 800, color: COLORS.primary, mb: 3 }}>
                  مركز الأطلال للسمع
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', color: '#64748b', mb: 3, lineHeight: 1.8 }}>
                  على مدار سنوات, بنينا جسوراً من الثقة مع مراجعينا. شعارنا ليس مجرد رمز، بل هو التزام بتقديم أفضل مستويات الرعاية الصحية السمعية. نحن نؤمن أن كل مريض يستحق اهتماماً خاصاً وحلولاً دقيقة تناسب نمط حياته.
                </Typography>
                <Stack spacing={2}>
                  {['تشخيص دقيق باستخدام أحدث الأجهزة', 'طاقم طبي متخصص ذو خبرة عالية', 'ضمان الجودة'].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <VerifiedIcon sx={{ color: COLORS.secondary }} />
                      <Typography variant="body1" fontWeight="600" color={COLORS.text}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: COLORS.primary, mb: 2 }}>
              نسعد بتواصلكم وزيارتكم
            </Typography>
            <Typography variant="body1" color="text.secondary">
              نحن هنا للرد على استفساراتكم وتقديم الدعم في أي وقت
            </Typography>
          </Box>
          {/* تم التعديل هنا: صورة يمين ومعلومات التواصل يسار في اللابتوب باستخدام row-reverse */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column-reverse', md: 'row-reverse' }, 
            alignItems: 'stretch', 
            gap: 6 
          }}>
            {/* حاوية الصورة على اليمين في اللابتوب */}
            <Box sx={{ width: { xs: '100%', md: '50%' } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}>
                <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', height: '100%', minHeight: 400 }}>
                  <img src={img1} alt="عنوان مركز الأطلال للسمع" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </Paper>
              </motion.div>
            </Box>

            {/* حاوية بطاقات التواصل على اليسار في اللابتوب */}
            <Box sx={{ width: { xs: '100%', md: '50%' } }}>
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                <Stack spacing={3} sx={{ height: '100%', justifyContent: 'center' }}>
                  <Paper sx={{ p: 4, display: 'flex', alignItems: 'flex-start', gap: 3, borderRadius: 4, bgcolor: COLORS.bgLight, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ p: 2, bgcolor: alpha(COLORS.primary, 0.1), borderRadius: '50%' }}>
                      <LocationOnIcon sx={{ color: COLORS.primary, fontSize: 35 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={COLORS.primary} mb={1}>عنوان المركز</Typography>
                      <Typography variant="body1" color="text.secondary" fontSize="1.1rem" lineHeight={1.6}>
                        الجمهورية العربية السورية - دمشق <br />
                        البحصة - بناء برج المستقبل - طابق 3
                      </Typography>
                    </Box>
                  </Paper>
                  <Paper sx={{ p:1, display: 'flex', alignItems: 'flex-start', gap: 3, borderRadius: 4, bgcolor: COLORS.bgLight, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ p: 2, bgcolor: alpha(COLORS.secondary, 0.1), borderRadius: '50%' }}>
                      <PhoneIcon sx={{ color: COLORS.secondary, fontSize: 35 }} />
                    </Box>
                    <Box >
                      <Typography variant="h5" fontWeight="700" color={COLORS.primary} mb={1}>رقم التواصل</Typography>
                      <Typography  variant="body1" color="text.secondary" fontSize="1.2rem" fontWeight="600" dir="ltr" textAlign="right">
                        +963 988 448 687 
                      </Typography>
                    </Box>
                  </Paper>
                </Stack>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

  
      <Box sx={{ py: { xs: 8, md: 12 } }}>
      
     
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column-reverse', md: 'row-reverse' }, 
            alignItems: 'center', 
            gap: 6 
          }}>
       
            <Box sx={{ width: { xs: '100%', md: '45%' } }}>
              <Paper elevation={0} sx={{ 
                borderRadius: 4, 
                overflow: 'hidden', 
                boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                position: 'relative'
              }}>
                <img src={img3} alt="معلومات مركز الأطلال" style={{ width: '100%', height: 'auto', display: 'block' }} />
           
                
              </Paper>
            </Box>

      
            <Box sx={{ width: { xs: '100%', md: '55%' } }}>
              <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                <Typography variant="h6" sx={{ color: COLORS.secondary, fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon /> من نحن
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: COLORS.primary, mb: 3 }}>
                  تكنولوجيا متطورة ورعاية مستدامة
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', color: '#64748b', mb: 3, lineHeight: 1.8 }}>
                  مركز الأطلال للسمع يعتبر من المراكز الرائدة في توفير أحدث السماعات الطبية الرقمية المتطورة (الداخلية والخارجية) التي تلائم جميع درجات نقص السمع وتتناسب مع كافة الأعمار.
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', color: '#64748b', mb: 4, lineHeight: 1.8 }}>
                  نقوم بإجراء فحوصات سمعية شاملة لتخطيط السمع، ونوفر قوالب سيليكونية ومطاطية بأيادٍ خبيرة، بالإضافة لخدمات الصيانة الدورية الدقيقة والبرمجة بالكمبيوتر لضمان أفضل أداء لسماعتك الطبية.
                </Typography>
              </motion.div>
            </Box>
          </Box>
        
      </Box>

   
      

      <WhatsAppFloatingBtn />
      
      <Box sx={{ bgcolor: COLORS.primary, py: 4, textAlign: 'center', color: 'white' }}>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          جميع الحقوق محفوظة © {new Date().getFullYear()} مركز الأطلال للسمع
        </Typography>
      </Box>
    </Container>
  );
};

function App() {
  return (
    <>
      <Helmet>
        <title>مركز الأطلال للسمع - أفضل خدمات السمع في سوريا</title>
        <meta name="description" content="مركز الأطلال للسمع في دمشق - متخصصون في فحص السمع وتركيب أجهزة السمع الطبية بأحدث التقنيات العالمية. استشارات طبية متخصصة." />
        <meta name="keywords" content="مركز الأطلال، فحص سمع دمشق، سماعات طبية سوريا، أجهزة سمع، ضعف السمع" />
      </Helmet>
      
      <Router>
        <div className="smooth-scroll">
          <Routes>
            <Route path="/" element={<HomePage />} />
          </Routes>
          <Analytics />
        </div>
      </Router>
    </>
  );
}

export default App;
